import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  memo,
} from "react";
import { Stage, Container, Sprite } from "@pixi/react";
import "@pixi/events";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { OpenArenaDialog } from "../../redux/arena-dialog/action";
import Building from "./Building";
import { BuildingProvider, useBuildingStore } from "./BuildingStore";
import BuildingDetailDialog from "./BuildingDetailDialog";
import { BuildingApiType, PlayerBuildingState } from "../../types/buildings";
import useAuth from "../../layouts/MainLayout/hooks/useAuth";
import Dialog from "../../components/Dialog";
import { buildingSounds, SoundUtils } from "../../utils/soundUtils";
import { playHoverSfx, sfxVolumeFromSettings } from "../../utils/hoverSfx";
import { IsBeta, WinterMode } from "../../utils/Network";
import Snowfall from "react-snowfall";
import { buildings, mapDecorations } from "./mapData";
import {
  DESKTOP_BG,
  MOBILE_BG,
  DESKTOP_IMAGE_SIZE,
  MOBILE_IMAGE_SIZE,
  MAX_ZOOM,
  ZOOM_STEP,
  MOBILE_ZOOM_SENSITIVITY,
} from "./mapConfig";
import { clamp, getDistance, getMidpoint, swallowGhostClick } from "./mapUtils";
import { BuildCostPreview, buildInstantUpgradeCostParts } from "./BuildCost";
import { useVillageLayoutEditor, VillageLayoutEditor } from "./LayoutEditor";
import { BuildingType } from "./types";
import { useAppTranslation } from "../../i18n/useAppTranslation";

/**
 * VillageMap – drag‑to‑scroll across desktop & mobile
 * ------------------------------------------------------------------
 * • Users can grab the map itself and pan in any direction.
 * • Map movement is clamped so you never see empty space.
 * • Hover (desktop) or tap (mobile) still highlights buildings.
 * • Zoom in/out with mouse wheel or pinch gestures.
 * ------------------------------------------------------------------
 *
 * Architecture Overview:
 * ------------------------------------------------------------------
 * This village map implementation follows a modular design pattern:
 *
 * 1. BuildingProvider (BuildingStore.tsx):
 *    - Context provider that manages all building-related state and operations
 *    - Handles API calls through BuildingService
 *    - Provides methods for getting buildings, upgrading, repairing, collecting, etc.
 *
 * 2. Components:
 *    - Building.tsx: Renders an individual building with its markers and status
 *    - BuildingDetailDialog.tsx: Displays building details and action options
 *    - BuildingDetailTab.tsx: Manages tabs in the detail dialog
 *    - ZoomControls.tsx: UI controls for map navigation
 *    - BuildingStatus.tsx: Shows building level and progress
 *    - BuildingMarker.tsx: Displays building type icon
 *
 * 3. Service:
 *    - BuildingService.ts: Handles all API calls related to buildings
 *
 * Static map data, configuration constants and helpers used to live here as
 * well; they now live alongside this file:
 *    - types.ts: shared BuildingType / MapDecoration interfaces
 *    - mapData.ts: the `buildings` and `mapDecorations` definitions
 *    - mapConfig.ts: background images, image sizes and zoom constraints
 *    - mapUtils.ts: clamp + pinch-gesture math helpers
 *    - BuildCost.tsx: cost rendering for the build / instant-upgrade dialogs
 *
 * This modular approach improves code maintainability and enables easy updates.
 * ------------------------------------------------------------------
 */

/**
 * Main Village Component
 */
const Village: React.FC = () => {
  return (
    <BuildingProvider>
      <ZoomableMap />
    </BuildingProvider>
  );
};

/**
 * ZoomableMap component - handles zooming and dragging
 * Memoized for performance
 */
const ZoomableMap: React.FC = memo(() => {
  // Access building store
  const {
    buildings: storeBuildings,
    getBuildings,
    getBuilding,
    detailDialog,
    closeDetailDialog,
    upgradeBuilding,
    instantUpgradeBuilding,
    getInstantUpgradePrice,
    repairBuilding,
    collectFromBuilding,
    buildBuilding,
    openConfirmDialog,
    confirmDialog,
    closeConfirmDialog,
    detailLoading,
    publicLoading,
  } = useBuildingStore();
  const { t } = useAppTranslation(["common", "village"]);

  // Detect mobile devices
  const [isMobile, setIsMobile] = useState(false);

  // Add viewport meta tag for mobile optimization
  useEffect(() => {
    if (isMobile) {
      // Ensure viewport meta tag prevents browser zoom
      let viewportMeta = document.querySelector('meta[name="viewport"]');
      if (!viewportMeta) {
        viewportMeta = document.createElement('meta');
        viewportMeta.setAttribute('name', 'viewport');
        document.head.appendChild(viewportMeta);
      }
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');

      // Prevent context menu on long press
      const preventContextMenu = (e: Event) => e.preventDefault();
      document.addEventListener('contextmenu', preventContextMenu);

      return () => {
        document.removeEventListener('contextmenu', preventContextMenu);
      };
    }
  }, [isMobile]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getMe } = useAuth();

  // Get user settings for sound effects volume
  const userSettings = useSelector((state: any) => state.userSettings);
  const soundVolume = userSettings?.soundEffectsVolume || 0.5;
  // Hover follows the settings slider directly (0 mutes it), unlike the click
  // volume above which has always had its own fallback.
  const hoverVolume = sfxVolumeFromSettings(userSettings);
  const getBuildingName = useCallback(
    (building: BuildingType | null | undefined) => {
      if (!building) return "";
      return t(`village:buildings.${building.id}.name`, {
        defaultValue: building.name,
      });
    },
    [t]
  );
  const localizedBuildings = useMemo(
    () =>
      buildings.map((building) => ({
        ...building,
        name: getBuildingName(building),
      })),
    [getBuildingName]
  );

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse), (max-width: 767px)");
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
      // Show mobile instructions for first-time mobile users
      if (e.matches && !localStorage.getItem('mobileInstructionsShown')) {
        setShowMobileInstructions(true);
        localStorage.setItem('mobileInstructionsShown', 'true');
        setTimeout(() => setShowMobileInstructions(false), 4000);
      }
    };
    handler(mq);
    mq.addEventListener
      ? mq.addEventListener("change", handler)
      : mq.addListener(handler);
    return () => {
      mq.removeEventListener
        ? mq.removeEventListener("change", handler)
        : mq.removeListener(handler);
    };
  }, []);

  // Get viewport dimensions
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  // Background image and original dimensions
  const bg = isMobile ? MOBILE_BG : DESKTOP_BG;
  const originalImageSize = isMobile ? MOBILE_IMAGE_SIZE : DESKTOP_IMAGE_SIZE;

  // Calculate minimum zoom to ensure the map covers the entire viewport
  const MIN_ZOOM = Math.max(
    viewportW / originalImageSize.w,
    viewportH / originalImageSize.h
  );

  // Calculate map dimensions - use original size since we'll scale with Container
  const mapW = originalImageSize.w;
  const mapH = originalImageSize.h;

  // Map state
  const [mapPos, setMapPos] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [highlight, setHighlight] = useState<number | null>(null);
  const [wasDragging, setWasDragging] = useState(false);
  const [isPinching, setIsPinching] = useState(false);
  const [lastPinchDistance, setLastPinchDistance] = useState(0);

  const [showMobileInstructions, setShowMobileInstructions] = useState(false);
  const buildingsData: BuildingApiType[] = useSelector(
    (state: any) => state.buildings
  );
  // Beta-only: lets testers reposition buildings on the map and export the
  // values. Off beta it returns `buildings` untouched.
  const layoutEditor = useVillageLayoutEditor(buildings, IsBeta);
  // Refs
  const stageRef = useRef<any>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startPointerX: number;
    startPointerY: number;
    pointerX: number;
    pointerY: number;
    dragDistance: number;
  } | null>(null);
  const touchesRef = useRef<Map<number, { x: number; y: number }>>(new Map());

  // Load buildings when component mounts
  useEffect(() => {
    if (localStorage.getItem("user.token")) {
      getBuildings();
    }

    // Preload building sound effects for better performance
    const preloadSounds = async () => {
      try {
        await SoundUtils.preloadSounds(Object.values(buildingSounds));
      } catch (error) {
        console.error("Error preloading building sounds:", error);
      }
    };
    preloadSounds();
  }, []);

  // Load building data without debug logging
  useEffect(() => {
    // Buildings data is now loaded
  }, [storeBuildings]);

  // Initialize map position
  useEffect(() => {
    // Calculate the position to center the map at minimum zoom
    const mapWidthAtMinZoom = mapW * MIN_ZOOM;
    const mapHeightAtMinZoom = mapH * MIN_ZOOM;

    // Center the map in the viewport
    const centerX = (viewportW - mapWidthAtMinZoom) / 2;
    const centerY = (viewportH - mapHeightAtMinZoom) / 2;

    // Set initial position
    setMapPos({ x: centerX, y: centerY });
  }, [viewportW, viewportH, mapW, mapH]);

  // Wrapper style
  const wrapperStyle: React.CSSProperties = {
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
    touchAction: "none",
    cursor: isDragging ? "grabbing" : isHovering ? "grab" : "default",
    // Prevent browser zoom on mobile
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    userSelect: "none",
  };

  // Function to zoom out to minimum zoom and center the map
  const zoomToFullMap = useCallback(() => {
    // Calculate the position to center the map at minimum zoom
    const mapWidthAtMinZoom = mapW * MIN_ZOOM;
    const mapHeightAtMinZoom = mapH * MIN_ZOOM;

    // Center the map in the viewport
    const centerX = (viewportW - mapWidthAtMinZoom) / 2;
    const centerY = (viewportH - mapHeightAtMinZoom) / 2;

    // Set new position and zoom
    setZoom(MIN_ZOOM);
    setMapPos({ x: centerX, y: centerY });
  }, [mapW, mapH, viewportW, viewportH]);

  // Zoom in function
  const zoomIn = useCallback(() => {
    const newZoom = clamp(zoom + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
    if (newZoom !== zoom) {
      // Zoom towards center of viewport
      const centerX = viewportW / 2;
      const centerY = viewportH / 2;

      // Calculate point on map under center at current zoom
      const pointOnMapX = (centerX - mapPos.x) / zoom;
      const pointOnMapY = (centerY - mapPos.y) / zoom;

      // Calculate new map position to keep the point under center
      const newMapX = centerX - pointOnMapX * newZoom;
      const newMapY = centerY - pointOnMapY * newZoom;

      // Calculate boundaries
      const minX = Math.min(0, viewportW - mapW * newZoom);
      const minY = Math.min(0, viewportH - mapH * newZoom);

      // Clamp position
      const clampedX = clamp(newMapX, minX, 0);
      const clampedY = clamp(newMapY, minY, 0);

      setZoom(newZoom);
      setMapPos({ x: clampedX, y: clampedY });
    }
  }, [zoom, mapPos, viewportW, viewportH, mapW, mapH]);

  // Zoom out function
  const zoomOut = useCallback(() => {
    const newZoom = clamp(zoom - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
    if (newZoom !== zoom) {
      // Zoom towards center of viewport
      const centerX = viewportW / 2;
      const centerY = viewportH / 2;

      // Calculate point on map under center at current zoom
      const pointOnMapX = (centerX - mapPos.x) / zoom;
      const pointOnMapY = (centerY - mapPos.y) / zoom;

      // Calculate new map position to keep the point under center
      const newMapX = centerX - pointOnMapX * newZoom;
      const newMapY = centerY - pointOnMapY * newZoom;

      // Calculate boundaries
      const minX = Math.min(0, viewportW - mapW * newZoom);
      const minY = Math.min(0, viewportH - mapH * newZoom);

      // Clamp position
      const clampedX = clamp(newMapX, minX, 0);
      const clampedY = clamp(newMapY, minY, 0);

      setZoom(newZoom);
      setMapPos({ x: clampedX, y: clampedY });
    }
  }, [zoom, mapPos, viewportW, viewportH, mapW, mapH]);

  // Handle wheel zoom - only for desktop
  const handleWheel = (e: WheelEvent) => {
    if (isMobile) return; // Skip wheel zoom on mobile

    e.preventDefault();

    // Determine zoom direction
    const delta = -Math.sign(e.deltaY) * ZOOM_STEP;
    const newZoom = clamp(zoom + delta, MIN_ZOOM, MAX_ZOOM);

    if (newZoom !== zoom) {
      // Calculate pointer position relative to map content
      const pointerX = e.clientX;
      const pointerY = e.clientY;

      // Calculate point on map under pointer at current zoom
      const pointOnMapX = (pointerX - mapPos.x) / zoom;
      const pointOnMapY = (pointerY - mapPos.y) / zoom;

      // Calculate new map position to keep the point under the pointer
      const newMapX = pointerX - pointOnMapX * newZoom;
      const newMapY = pointerY - pointOnMapY * newZoom;

      // Calculate boundaries to prevent showing empty space
      const minX = Math.min(0, viewportW - mapW * newZoom);
      const minY = Math.min(0, viewportH - mapH * newZoom);

      // Clamp position
      const clampedX = clamp(newMapX, minX, 0);
      const clampedY = clamp(newMapY, minY, 0);

      // Update state
      setZoom(newZoom);
      setMapPos({ x: clampedX, y: clampedY });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      zoomToFullMap();
    }, 2000);
  }, []);

  // Handle stage initialization
  const handleStageMount = (instance: any) => {
    if (!instance) return;

    stageRef.current = instance;

    // Add wheel event listener to stage only for desktop
    if (!isMobile) {
      const canvas = instance.app.view;
      canvas.addEventListener("wheel", handleWheel);

      return () => {
        canvas.removeEventListener("wheel", handleWheel);
      };
    }
  };

  // Mobile touch and zoom handling
  useEffect(() => {
    if (!isMobile || !stageRef.current) return;

    const canvas = stageRef.current.app.view;

    // Handle touch events for pinch-to-zoom
    const handleTouchStart = (e: TouchEvent) => {
      const touches = Array.from(e.touches);

      // Update touches reference
      touchesRef.current.clear();
      touches.forEach((touch) => {
        touchesRef.current.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
      });

      if (touches.length === 2) {
        // Start pinch gesture
        e.preventDefault();
        setIsPinching(true);
        setIsDragging(false);

        const touch1 = { x: touches[0].clientX, y: touches[0].clientY };
        const touch2 = { x: touches[1].clientX, y: touches[1].clientY };

        const distance = getDistance(touch1, touch2);

        setLastPinchDistance(distance);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touches = Array.from(e.touches);

      if (touches.length === 2 && isPinching) {
        e.preventDefault();

        const touch1 = { x: touches[0].clientX, y: touches[0].clientY };
        const touch2 = { x: touches[1].clientX, y: touches[1].clientY };

        const distance = getDistance(touch1, touch2);
        const center = getMidpoint(touch1, touch2);

        if (lastPinchDistance > 0) {
          // Calculate zoom delta
          const zoomDelta = (distance - lastPinchDistance) * MOBILE_ZOOM_SENSITIVITY;
          const newZoom = clamp(zoom + zoomDelta, MIN_ZOOM, MAX_ZOOM);

          if (newZoom !== zoom) {
            // Calculate point on map under pinch center at current zoom
            const pointOnMapX = (center.x - mapPos.x) / zoom;
            const pointOnMapY = (center.y - mapPos.y) / zoom;

            // Calculate new map position to keep the point under the pinch center
            const newMapX = center.x - pointOnMapX * newZoom;
            const newMapY = center.y - pointOnMapY * newZoom;

            // Calculate boundaries to prevent showing empty space
            const minX = Math.min(0, viewportW - mapW * newZoom);
            const minY = Math.min(0, viewportH - mapH * newZoom);

            // Clamp position
            const clampedX = clamp(newMapX, minX, 0);
            const clampedY = clamp(newMapY, minY, 0);

            // Update state
            setZoom(newZoom);
            setMapPos({ x: clampedX, y: clampedY });
          }
        }

        setLastPinchDistance(distance);
      } else if (touches.length > 1) {
        // Prevent default behavior for multi-touch
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touches = Array.from(e.touches);

      if (touches.length < 2) {
        setIsPinching(false);
        setLastPinchDistance(0);
      }

      // Update touches reference
      touchesRef.current.clear();
      touches.forEach((touch) => {
        touchesRef.current.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
      });
    };

    // Add event listeners
    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, zoom, mapPos, lastPinchDistance, isPinching, mapW, mapH, viewportW, viewportH]);

  // Handle map dragging
  const handlePointerDown = (event: any) => {
    // Don't start dragging if we're pinching
    if (isPinching) return;

    setIsDragging(true);
    setWasDragging(false);
    document.body.style.cursor = "grabbing";

    const pointerX = event.clientX || event.global?.x;
    const pointerY = event.clientY || event.global?.y;

    dragRef.current = {
      startX: mapPos.x,
      startY: mapPos.y,
      startPointerX: pointerX,
      startPointerY: pointerY,
      pointerX: pointerX,
      pointerY: pointerY,
      dragDistance: 0,
    };
  };

  const handlePointerMove = (event: any) => {
    if (!isDragging || !dragRef.current || isPinching) return;

    // Calculate pointer movement
    const clientX = event.clientX || event.global?.x;
    const clientY = event.clientY || event.global?.y;

    const dx = clientX - dragRef.current.pointerX;
    const dy = clientY - dragRef.current.pointerY;

    // Update drag distance
    dragRef.current.dragDistance = Math.sqrt(dx * dx + dy * dy);

    // Mark as dragging if moved more than 5 pixels
    if (dragRef.current.dragDistance > 5) {
      setWasDragging(true);
    }
    // Calculate boundaries to prevent showing empty space
    const minX = Math.min(0, viewportW - mapW * zoom);
    const minY = Math.min(0, viewportH - mapH * zoom);
    // Calculate new position
    const newX = clamp(dragRef.current.startX + dx, minX, 0);
    const newY = clamp(dragRef.current.startY + dy, minY, 0);
    setMapPos({ x: newX, y: newY });
  };

  const handlePointerUp = (event: any) => {
    setIsDragging(false);
    document.body.style.cursor = "auto";

    // Check if the pointer position changed from start to end
    if (dragRef.current) {
      const currentPointerX = event.clientX || event.global?.x;
      const currentPointerY = event.clientY || event.global?.y;

      // Calculate the distance moved from start position
      const dx = currentPointerX - dragRef.current.startPointerX;
      const dy = currentPointerY - dragRef.current.startPointerY;
      const totalDistance = Math.sqrt(dx * dx + dy * dy);

      // If moved more than 5 pixels, consider it a drag
      if (totalDistance > 5) {
        setWasDragging(true);
      }
    }

    // Reset wasDragging after a short delay to allow tap events to process
    setTimeout(() => {
      setWasDragging(false);
      dragRef.current = null;
    }, 50);
  };

  // Sprite event handlers - with useCallback to prevent recreating functions
  //
  // These fire from both onpointerover *and* onpointermove, so the hover voice
  // is gated on the hovered building actually changing — otherwise every mouse
  // move across a roof would retrigger it.
  const hoveredBuildingRef = useRef<number | null>(null);
  const handleSpriteOver = useCallback(
    (id: number) => (event: any) => {
      if (event?.pointerType === "touch") return;
      // Pixi tracks pointer moves on the whole document and hit-tests the map
      // at that spot, so a building covered by a dialog, drawer or HUD panel
      // still gets hovered. Only react when the canvas is actually on top.
      const canvas = stageRef.current?.app?.view;
      if (
        canvas &&
        Number.isFinite(event?.clientX) &&
        document.elementFromPoint(event.clientX, event.clientY) !== canvas
      ) {
        hoveredBuildingRef.current = null;
        setHighlight(null);
        return;
      }
      setHighlight(id);
      if (hoveredBuildingRef.current === id) return;
      hoveredBuildingRef.current = id;
      // Seeded by building id, so each plot answers with its own note.
      playHoverSfx("building", { volume: hoverVolume, seed: id });
    },
    [hoverVolume]
  );

  const handleSpriteOut = useCallback(() => {
    hoveredBuildingRef.current = null;
    setHighlight(null);
  }, []);

  const handleSpriteTap = useCallback(
    (id: number) => (event: any) => {
      // Prevent click if user was dragging the map
      if (wasDragging) {
        return;
      }

      // Every branch below either opens a dialog or navigates, so on touch the
      // browser's follow-up click would land on the new DOM under the finger.
      if (event?.pointerType === "touch") swallowGhostClick();

      const staticBuilding = buildings.find((b) => b.id === id);
      if (staticBuilding?.type === "static" && staticBuilding?.link) {
        // Arena opens the Arena dashboard dialog instead of navigating away.
        if (staticBuilding.link === "/arena") {
          return dispatch(OpenArenaDialog({ open: true }));
        }
        return navigate(staticBuilding.link);
      }

      if (
        buildingsData.find((b) => b.buildingId === id)?.state ===
        PlayerBuildingState.Upgrading
      ) {
        return;
      }

      // Find building information
      const building = buildings.find((b) => b.id === id);
      if (!building) return;
      const buildingName = getBuildingName(building);
      const localizedBuilding = { ...building, name: buildingName };

      // Match the static building data with API data
      const apiBuilding = storeBuildings?.find((b) => b.buildingId === id);

      // If building state is NotHave, directly open build confirm dialog
      if (apiBuilding && apiBuilding.state === PlayerBuildingState.NotHave) {
        openConfirmDialog({
          title: t("village:dialogs.buildConfirmation.title"),
          msg: (
            <>
              {t("village:dialogs.buildConfirmation.message", {
                name: buildingName,
              })}
              <BuildCostPreview building={apiBuilding} />
            </>
          ),
          submit: async () => {
            try {
              await buildBuilding(apiBuilding.buildingId);
              // Note: buildBuilding already handles dialog closure and data refresh
            } catch (error) {
              console.error("Error building:", error);
            }
          },
        });
        return;
      }

      // Special handling for barracks - navigate directly
      if (apiBuilding && apiBuilding.buildingId === 6) {
        return navigate("/barracks");
      }

      // Special handling for research building - navigate to /research
      if (building.id === 7 || apiBuilding?.buildingId === 7) {
        return navigate("/research");
      }

      // Check if building is collectable (more than 20% filled)
      const isCollectable =
        apiBuilding &&
        apiBuilding.state !== 0 &&
        apiBuilding.buildingId !== 6 &&
        apiBuilding.currentMaxCapacity > 0 &&
        (apiBuilding.currentCapacity * 100) / apiBuilding.currentMaxCapacity >
        20;

      // If building is collectable, collect directly on first click
      if (isCollectable && apiBuilding) {
        openConfirmDialog({
          title: t("village:dialogs.collectResources.title"),
          msg: t("village:dialogs.collectResources.message", {
            name: buildingName,
          }),
          submit: async () => {
            try {
              await collectFromBuilding(apiBuilding.id);
              // Note: collectFromBuilding already handles dialog closure and data refresh
            } catch (error) {
              console.error("Error collecting from building:", error);
            }
          },
        });
        return;
      }

      // Default behavior: Show building detail dialog
      if (apiBuilding) {
        getBuilding(apiBuilding.id, { ...localizedBuilding, ...apiBuilding });
      } else {
        getBuilding(id, localizedBuilding);
      }
    },
    [
      buildings,
      storeBuildings,
      getBuilding,
      buildingsData,
      wasDragging,
      openConfirmDialog,
      collectFromBuilding,
      buildBuilding,
      getMe,
      getBuildings,
      getBuildingName,
      navigate,
      dispatch,
      t,
    ]
  );

  // Handle collection from building - memoized to prevent unnecessary recreations
  const handleCollectMine = useCallback(
    (id: number | string) => {
      if (id) {
        collectFromBuilding(id).then(() => {
          getMe();
          getBuildings();
        });
      }
    },
    [collectFromBuilding, getBuildings]
  );

  // Handle repair building - memoized to prevent unnecessary recreations
  const handleRepairBuilding = useCallback(
    (id: number | string) => {
      if (id) {
        repairBuilding(id).then(() => {
          getBuildings();
        });
      }
    },
    [repairBuilding, getBuildings]
  );

  // Handle instant upgrade building - memoized to prevent unnecessary recreations
  const handleInstantUpgrade = useCallback(
    async (id: number | string) => {
      if (!id) return;

      try {
        // Get pricing information from API
        const priceData = await getInstantUpgradePrice(id);

        if (!priceData) {
          openConfirmDialog({
            title: t("village:dialogs.error.title"),
            msg: t("village:dialogs.instantUpgrade.errors.pricingUnavailable"),
            submit: null,
          });
          return;
        }

        // Extract costs from the API response - try different possible property names
        const clegCost =
          priceData.clegCost || priceData.totalClegCost || priceData.cleg || 0;
        const stoneCost =
          priceData.stoneCost ||
          priceData.totalStoneCost ||
          priceData.stone ||
          0;
        const ironCost =
          priceData.ironCost || priceData.totalIronCost || priceData.iron || 0;

        // Create cost display message
        const costParts = buildInstantUpgradeCostParts(
          clegCost,
          stoneCost,
          ironCost
        );

        openConfirmDialog({
          title: t("village:dialogs.instantUpgrade.title"),
          msg: (
            <>
              {t("village:dialogs.instantUpgrade.confirmMessage")}{" "}
              {costParts}
            </>
          ),
          submit: () => instantUpgradeBuilding(id),
        });
      } catch (error) {
        console.error("Error getting instant upgrade price:", error);
        openConfirmDialog({
          title: t("village:dialogs.error.title"),
          msg: t("village:dialogs.instantUpgrade.errors.pricingFailed"),
          submit: null,
        });
      }
    },
    [instantUpgradeBuilding, getInstantUpgradePrice, openConfirmDialog, t]
  );

  return (
    <div style={wrapperStyle}>
      <Stage
        width={viewportW}
        height={viewportH}
        options={{ backgroundColor: 0x000000 }}
        ref={handleStageMount}
      >
        <Container
          x={mapPos.x}
          y={mapPos.y}
          scale={zoom}
          eventMode="static"
          cursor={isDragging ? "grabbing" : "grab"}
          onpointerdown={handlePointerDown}
          onpointermove={handlePointerMove}
          onpointerup={handlePointerUp}
          onpointerupoutside={handlePointerUp}
          onpointerover={() => setIsHovering(true)}
          onpointerout={() => setIsHovering(false)}
        >
          {/* Background Map */}
          <Sprite image={bg} x={0} y={0} width={mapW} height={mapH} zIndex={-1} />

          {/* Map Decorations */}
          {mapDecorations.map((decoration) => (
            <Sprite
              key={decoration.id}
              image={decoration.image}
              x={decoration.x}
              y={decoration.y}
              scale={decoration.scale ?? 1}
              anchor={decoration.anchor ?? 0.5}
              zIndex={decoration.zIndex ?? 0}
              eventMode="none"
            />
          ))}

          {/* Buildings - Render only when necessary */}
          {layoutEditor.buildings.map((layoutBuilding, index) => {
            // Unedited buildings come back from the layout editor as the
            // original objects; use their translated copies so the memoised
            // sprites keep a stable identity.
            const building = layoutEditor.isChanged(layoutBuilding.id)
              ? { ...layoutBuilding, name: getBuildingName(layoutBuilding) }
              : localizedBuildings[index];
            if (building.disabled) return null;
            // Find matching API building data
            const apiBuilding = storeBuildings?.find(
              (b) => b.buildingId === building.id
            );

            // Check if this building is currently being collected
            const isCollecting = publicLoading === apiBuilding?.id;

            return (
              <Building
                key={building.id}
                building={building}
                highlight={highlight ?? layoutEditor.selectedId}
                buildingData={apiBuilding}
                handleSpriteOver={handleSpriteOver}
                handleSpriteOut={handleSpriteOut}
                handleSpriteTap={handleSpriteTap}
                onCollect={handleCollectMine}
                onRepair={handleRepairBuilding}
                onInstantUpgrade={handleInstantUpgrade}
                isCollecting={isCollecting}
                soundVolume={soundVolume}

              />
            );
          })}
        </Container>
      </Stage>

      {IsBeta && <VillageLayoutEditor editor={layoutEditor} />}

      {/* Building Detail Dialog */}
      <BuildingDetailDialog
        refreshData={getBuildings}
        refreshUserInfo={getMe}
        open={detailDialog.open}
        onClose={closeDetailDialog}
        building={detailDialog.item}
        onUpgrade={upgradeBuilding}
        onRepair={repairBuilding}
        onCollect={collectFromBuilding}
        onBuild={buildBuilding}
        loading={!!publicLoading}
        detailLoading={detailLoading}
        storeBuildings={storeBuildings}
        staticBuildings={localizedBuildings}
        getBuilding={getBuilding}
      />


      {/* Zoom Level Indicator - Mobile only (bottom-left, clear of the HUD) */}
      {isMobile && (
        <div
          style={{
            position: "absolute",
            bottom: "140px",
            left: "12px",
            backgroundColor: "rgba(0,0,0,0.55)",
            color: "white",
            padding: "4px 10px",
            borderRadius: "14px",
            fontSize: "12px",
            zIndex: 1000,
            fontFamily: "monospace",
          }}
        >
          {Math.round(zoom * 100)}%
        </div>
      )}

      {/* Confirm Dialog - Missing component that was causing the issue */}
      <Dialog
        loading={!!publicLoading}
        title={confirmDialog?.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={confirmDialog?.submit}
        open={confirmDialog?.open}
        onClose={closeConfirmDialog}
      >
        {confirmDialog?.msg}
      </Dialog>

      {WinterMode && (
        <Snowfall
          // Changes the snowflake colorF
          color="white"
          // Applied to the canvas element
          // Controls the number of snowflakes that are created (default 150)
          snowflakeCount={400}
          radius={[0.5, 0.5]}
          speed={[0.5, 1.5]}
        />
      )}
    </div>
  );
});

export default Village;
